import { AccessFunctionality, RequestStatus, defaults, entities, getAPIMethod } from 'typed/api';
import { ExtractAction, IAction, NetworkAction, entityActionSet, networkAction } from './utils';

export { voidSession, failedAPIResponse } from 'actions/app';

type AddAllPublicExternalLinks = NetworkAction<
'ADD_ALL_PUBLIC_EXTERNAL_LINKS',
{ entities: entities.PublicExternalLink[]; sync?: boolean; reloadAfterSync?: boolean; },
entities.PublicExternalLink[]
>;

export const
    sessionInformation = {
        setWorkshopLanguageCode: (workshopID: number, languageCode: string) =>
            networkAction('SET_WORKSHOP_LANGUAGE_CODE', { workshopID, languageCode }, getAPIMethod('SessionInformation', 'setWorkshopLanguageCode'), workshopID, languageCode),
        clearWorkshopLanguageCode: () =>
            networkAction('CLEAR_WORKSHOP_LANGUAGE_CODE', getAPIMethod('SessionInformation', 'clearWorkshopLanguageCode'))
    },

    loadAppInitialData = () => networkAction('LOAD_APP_INITIAL_DATA', getAPIMethod<string>('SessionInformation', 'loadInitialData')),

    userSyncExcelSettings = {
        ...entityActionSet(defaults.UserSyncExcelSettings, {
            loadAll: networkAction('LOAD_ALL_USER_SYNC_EXCEL_SETTINGS', getAPIMethod('UserSyncExcelSettings', 'loadAll'))
        }),
        rerunUserSync: (entityID: number) =>
            networkAction('RERUN_USER_SYNC', { entityID }, getAPIMethod('UserSyncExcelSettings', 'rerunUserSyncFromExternalFile'), entityID)
    },

    { loadAll: loadAllSystemUserTypes } = entityActionSet(defaults.SystemUserType, {
        loadAll: networkAction('LOAD_ALL_SYSTEM_USER_TYPES', getAPIMethod('SystemUserType', 'loadAll'))
    }),

    { loadAll: loadAllSystemUserRoles } = entityActionSet(defaults.SystemUserRole, {
        loadAll: networkAction('LOAD_ALL_SYSTEM_USER_ROLES', getAPIMethod('SystemUserRole', 'loadAll'))
    }),

    userCompanyFunctionalityAccesses = {
        ...entityActionSet(defaults.UserCompanyFunctionalityAccess, {
            loadAll: networkAction('LOAD_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES', getAPIMethod('UserCompanyFunctionalityAccess', 'loadCurrentCompanyFunctionalityAccesses')),
            addAll: networkAction('ADD_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES', getAPIMethod('UserCompanyFunctionalityAccess', 'addAll')),
            editAll: networkAction('EDIT_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES', getAPIMethod('UserCompanyFunctionalityAccess', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES', getAPIMethod('UserCompanyFunctionalityAccess', 'deleteAll'))
        }),
        toggleSmartStrategyAccessFunctionality: (functionalityID: AccessFunctionality, active: boolean) =>
            networkAction(
                'TOGGLE_SMART_STRATEGY_ACCESS_FUNCTIONALITY',
                { functionalityID, active },
                getAPIMethod('UserCompanyFunctionalityAccess', 'toggleSmartStrategyAccessFunctionality'),
                functionalityID,
                active
            )
    },

    { loadAll: loadAllUserRoleBillingUserTypes } = entityActionSet(defaults.UserRoleBillingUserType, {
        loadAll: networkAction('LOAD_ALL_USER_ROLE_BILLING_USER_TYPES', getAPIMethod('UserRoleBillingUserType', 'loadAll'))
    }),

    { loadAll: loadAllUserInterfaceTranslationLanguages } = entityActionSet(defaults.UserInterfaceTranslationLanguage, {
        loadAll: networkAction('LOAD_ALL_USER_INTERFACE_TRANSLATION_LANGUAGES', getAPIMethod('UserInterfaceTranslationLanguage', 'loadAll'))
    }),

    { loadAll: loadAllQuickStarts } = entityActionSet(defaults.QuickStartReport, {
        loadAll: networkAction('LOAD_ALL_QUICK_STARTS', getAPIMethod('QuickStart', 'loadAll'))
    }),

    publicExternalLinks = {
        loadMany: (entityIDs: number[]) =>
            networkAction('LOAD_MANY_PUBLIC_EXTERNAL_LINKS', { entityIDs }, getAPIMethod<entities.PublicExternalLink>('PublicExternalLink', 'loadMany'), entityIDs),
        load: (identifierGuid: string) => networkAction(
            'LOAD_PUBLIC_EXTERNAL_LINK', { identifierGuid }, getAPIMethod<entities.PublicExternalLink>('PublicExternalLink', 'loadByIdentifierGuid'), identifierGuid
        ),
        loadSatisfactionFeedbackLink: () =>
            networkAction('LOAD_SATISFACTION_FEEDBACK_LINK', getAPIMethod<string>('PublicExternalLink', 'loadSatisfactionFeedbackLink')),
        ...entityActionSet(defaults.PublicExternalLink, {
            loadAll: networkAction('LOAD_ALL_PUBLIC_EXTERNAL_LINKS', getAPIMethod('PublicExternalLink', 'loadAll')),
            add: networkAction('ADD_PUBLIC_EXTERNAL_LINK', getAPIMethod('PublicExternalLink', 'addAndLoad')),
            edit: networkAction('EDIT_PUBLIC_EXTERNAL_LINK', getAPIMethod('PublicExternalLink', 'edit')),
            delete: networkAction('DELETE_PUBLIC_EXTERNAL_LINK', getAPIMethod('PublicExternalLink', 'delete'))
        }),
        addAll: (entities: entities.PublicExternalLink[], sync = false, reloadAfterSync = false): AddAllPublicExternalLinks =>
            async(dispatch: any) => {
                let entityIDs = entities.map(({ ID }) => ID);

                dispatch({
                    type: 'ADD_ALL_PUBLIC_EXTERNAL_LINKS',
                    payload: { entities, entityIDs, status: RequestStatus.UNSYNCED }
                });

                if (sync) {
                    entityIDs = await dispatch(networkAction(
                        'ADD_ALL_PUBLIC_EXTERNAL_LINKS',
                        { entities, entityIDs },
                        getAPIMethod<number>('PublicExternalLink', 'addAll'),
                        entities
                    ));
                }

                if (reloadAfterSync) {
                    return dispatch(publicExternalLinks.loadMany(entityIDs));
                }

                return entityIDs;
            }
    },

    { loadAll: loadAllCategories } = entityActionSet(defaults.CategoryReport, {
        loadAll: networkAction('LOAD_ALL_CATEGORIES', getAPIMethod('Category', 'loadAll'))
    }),

    loadUserActivityReports = (startDate: Date | null, endDate: Date | null) =>
        networkAction('LOAD_USER_ACTIVITY_REPORT', { startDate, endDate }, getAPIMethod<string>('UserActivityReport', 'loadByPeriod'), startDate, endDate);

export type AppAction = ExtractAction<
| typeof sessionInformation
| typeof userSyncExcelSettings
| typeof loadAppInitialData
| typeof loadAllSystemUserTypes
| typeof loadAllSystemUserRoles
| typeof userCompanyFunctionalityAccesses
| typeof loadAllUserRoleBillingUserTypes
| typeof loadAllUserInterfaceTranslationLanguages
| typeof loadAllQuickStarts
| typeof publicExternalLinks
| typeof loadAllCategories
| typeof loadUserActivityReports>
| IAction<'VOID_SESSION', void>
| IAction<'FAILED_API_RESPONSE', void>;
